import {
  BASE64_PATTERN,
  DENY_EXACT_KEYS,
  DENY_KEY_TOKENS,
  JWT_PATTERN,
  PUBLIC_TOKEN_PATTERN,
  REDACTED,
  URL_KEY_TOKENS,
  URL_USERINFO_PATTERN,
} from "./spec";

/**
 * Shared privacy scrubber for every Sentry event/breadcrumb leaving any
 * Reflecto surface (server relay, Chrome extension, web PWA).
 *
 * The invariant: plaintext notification content, ciphertext envelopes, keys,
 * and tokens must never reach an observability vendor. The extension and web
 * app hold DECRYPTED content, so their SDKs sit inside the trust boundary —
 * this module is the last line of defense on whatever reaches the SDK
 * pipeline, on top of each surface's collection lockdown (see each surface's
 * Sentry init module and docs/observability.md).
 *
 * Redaction is visible (`[scrubbed]`), never a silent delete, so a denylist
 * gap shows up in the Sentry UI as a scrubbed field instead of vanishing.
 *
 * Typed structurally (every field optional/unknown) so both @sentry/node and
 * @sentry/browser event types assign without this package depending on any
 * Sentry SDK. Mutates in place and returns the same reference — a clean
 * diagnostic event is byte-identical after scrubbing (asserted in tests).
 *
 * IN-PLACE MUTATION CAVEAT: the event handed to a beforeSend hook is owned
 * by the SDK, so mutating it is safe — but Sentry scopes merge setExtra/
 * setContext values BY REFERENCE. Never pass a live application object
 * (e.g. a decrypted notification also held in a store) to a Sentry scope:
 * this scrubber would redact it in place and corrupt app state. The
 * no-payload captureError APIs on extension/web exist precisely to make
 * that impossible at capture sites.
 */

export interface ScrubbableBreadcrumb {
  message?: unknown;
  data?: unknown;
}

export interface ScrubbableEvent {
  request?: unknown;
  user?: unknown;
  message?: unknown;
  transaction?: unknown;
  logentry?: unknown;
  exception?: unknown;
  breadcrumbs?: unknown;
  extra?: unknown;
  contexts?: unknown;
  tags?: unknown;
}

function tokenizeKey(key: string): string[] {
  return (
    key
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      // Acronym-to-word boundary: APIKey -> API Key, FCMToken -> FCM Token.
      // Without this, all-caps prefixes glue into one token ("apikey") that
      // never matches the denylist.
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((token) => token.length > 0)
  );
}

function keyIsDenied(key: string): boolean {
  if (DENY_EXACT_KEYS.has(key)) return true;
  return tokenizeKey(key).some(
    (token) => DENY_KEY_TOKENS.has(token) || DENY_KEY_TOKENS.has(token.replace(/s$/, ""))
  );
}

function isUrlKey(key: string): boolean {
  return tokenizeKey(key).some((token) => URL_KEY_TOKENS.has(token));
}

function stripQuery(url: string): string {
  // Cut at the query OR the fragment, whichever comes first: query strings
  // carry tokens (?token=…, ?since_id=…) and the web app's hash routes carry
  // conversation keys (#/c/:key) — both are content-adjacent.
  const queryStart = url.indexOf("?");
  const fragmentStart = url.indexOf("#");
  const cut =
    queryStart === -1
      ? fragmentStart
      : fragmentStart === -1
        ? queryStart
        : Math.min(queryStart, fragmentStart);
  return cut === -1 ? url : url.slice(0, cut);
}

/**
 * Redact token-shaped substrings from free text (exception messages,
 * breadcrumb messages, URLs). Order matters: the project token pattern and
 * JWT pattern run before the generic base64 rule so their redaction labels
 * win over the catch-all.
 */
export function scrubString(input: string): string {
  return input
    .replace(PUBLIC_TOKEN_PATTERN, "$1_$2_***")
    .replace(JWT_PATTERN, "[scrubbed:jwt]")
    .replace(URL_USERINFO_PATTERN, "$1[scrubbed]@")
    .replace(BASE64_PATTERN, "[scrubbed:base64]");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Recursively scrub a value in place. Object keys matching the denylist have
 * their whole value replaced with [scrubbed]; every surviving string value is
 * regex-scrubbed; URL-keyed strings additionally lose their query string.
 * Cycle-safe via `seen`.
 */
function scrubValue(value: unknown, keyHint: string | undefined, seen: WeakSet<object>): unknown {
  if (typeof value === "string") {
    const scrubbed = scrubString(value);
    return keyHint !== undefined && isUrlKey(keyHint) ? stripQuery(scrubbed) : scrubbed;
  }
  if (value === null || typeof value !== "object") return value;
  // Binary payloads (Buffer, typed arrays, ArrayBuffer) are ciphertext until
  // proven otherwise, and SDK normalizers would serialize them as byte
  // arrays. They carry no diagnostic value — drop them wholesale.
  if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) return REDACTED;
  // Error instances hide message/stack behind non-enumerable properties, so
  // the key walk below never sees them — but Sentry normalizers serialize
  // them AFTER beforeSend. Compress to a scrubbed one-liner instead.
  if (value instanceof Error) return `${value.name}: ${scrubString(value.message)}`;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubValue(value[i], undefined, seen);
    }
    return value;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (keyIsDenied(key)) {
      record[key] = REDACTED;
    } else {
      record[key] = scrubValue(record[key], key, seen);
    }
  }
  return record;
}

function scrubBreadcrumbInPlace<T extends ScrubbableBreadcrumb>(
  breadcrumb: T,
  seen: WeakSet<object>
): T {
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = scrubString(breadcrumb.message);
  }
  // Assign the return value — scrubValue redacts binary payloads and Error
  // instances by RETURNING a replacement, not by mutating them, so a
  // discarded return would let a breadcrumb whose whole `data` is a
  // Buffer/Error escape redaction (flagged on PR #737, fixed here).
  if (breadcrumb.data !== undefined) {
    breadcrumb.data = scrubValue(breadcrumb.data, undefined, seen);
  }
  return breadcrumb;
}

/**
 * `beforeBreadcrumb`-shaped hook. On surfaces that render decrypted content
 * (extension, web) breadcrumbs should be disabled entirely at integration
 * level and this used only as a backstop; on the server it scrubs console
 * breadcrumbs (structured log lines).
 */
export function scrubBreadcrumbPayload<T extends ScrubbableBreadcrumb>(breadcrumb: T): T {
  return scrubBreadcrumbInPlace(breadcrumb, new WeakSet());
}

/**
 * `beforeSend`-shaped hook. Walks every part of the event that can carry
 * request or application data. Diagnostic structure (error type, stack
 * frames, opaque IDs) passes through untouched.
 */
export function scrubEventPayload<T extends ScrubbableEvent>(event: T): T {
  const seen = new WeakSet<object>();

  if (isRecord(event.request)) {
    const request = event.request;
    // Request bodies must never ship. Body capture is disabled at SDK level
    // on every surface; if a body still materializes, replace it visibly
    // rather than trust it.
    if ("data" in request) request.data = REDACTED;
    if ("query_string" in request) request.query_string = REDACTED;
    if (isRecord(request.env) && "REMOTE_ADDR" in request.env) {
      // Client IPs are PII (see .claude/rules/security.md).
      request.env.REMOTE_ADDR = REDACTED;
    }
    scrubValue(request, "request", seen);
  }

  // No Reflecto surface sets user context, ever. Drop anything an SDK
  // internal added.
  delete event.user;

  if (typeof event.message === "string") {
    event.message = scrubString(event.message);
  }
  if (typeof event.transaction === "string") {
    // Route templates are fine; unparameterized paths can embed rfk_ tokens.
    event.transaction = scrubString(event.transaction);
  }

  if (isRecord(event.exception) && Array.isArray(event.exception.values)) {
    for (const exception of event.exception.values as unknown[]) {
      if (!isRecord(exception)) continue;
      if (typeof exception.value === "string") {
        // Exception messages sometimes interpolate input (e.g. a failed
        // JSON.parse echoing its argument) — regex-scrub, don't key-drop.
        exception.value = scrubString(exception.value);
      }
      if (isRecord(exception.stacktrace) && Array.isArray(exception.stacktrace.frames)) {
        for (const frame of exception.stacktrace.frames as unknown[]) {
          // Local variables can hold plaintext, ciphertext buffers, and
          // tokens. Collection is off at SDK level; this is the backstop.
          if (isRecord(frame) && frame.vars !== undefined) delete frame.vars;
        }
      }
      if (isRecord(exception.mechanism) && exception.mechanism.data !== undefined) {
        exception.mechanism.data = scrubValue(exception.mechanism.data, undefined, seen);
      }
    }
  }

  // captureMessage with params lands here instead of event.message.
  if (isRecord(event.logentry)) {
    if (typeof event.logentry.message === "string") {
      event.logentry.message = scrubString(event.logentry.message);
    }
    if (event.logentry.params) {
      event.logentry.params = scrubValue(event.logentry.params, undefined, seen);
    }
  }

  if (Array.isArray(event.breadcrumbs)) {
    for (const breadcrumb of event.breadcrumbs as unknown[]) {
      if (isRecord(breadcrumb)) scrubBreadcrumbInPlace(breadcrumb, seen);
    }
  }
  // Assign returns here too — same reason as the breadcrumb.data fix above:
  // a top-level container that is itself a Buffer/Error is redacted by
  // return value, not mutation.
  if (event.extra) event.extra = scrubValue(event.extra, undefined, seen);
  if (event.contexts) event.contexts = scrubValue(event.contexts, undefined, seen);
  if (event.tags) event.tags = scrubValue(event.tags, undefined, seen);

  return event;
}
